/* ============================================================================
 * Knife Steel Hub — interaction layer (ES module).
 * ENHANCES the server-rendered scatter + table; never builds them. No-JS users
 * keep the SSR scatter + table. Pure helpers are exported for node unit tests;
 * the DOM init is guarded so importing this file in node is side-effect-free.
 *
 * Honesty rule carried into the client: composition values come from the
 * #kns-hub-data island where not_disclosed/unknown cells are v:null. The UI
 * branches on `st` (state) and NEVER does arithmetic on a possibly-null value.
 * ========================================================================== */

// Progressive-enhancement map: the shared, deployed render module (the same code
// the approved prototype renders). The relative specifier resolves to /assets/ on
// the Shopify CDN, on disk for node tests, and under the preview server — ONE
// module across browser + node + preview (R1 C4/B13).
import { buildScene3d, buildScene2d, buildMapSvg, mapSceneTransform, mapConfig, steelAbbr } from './kns-steel-hub-map.js';

/* ---------------------------- pure helpers (tested) ---------------------- */

// BUILD STEEL-NAMES: the island carries `name` (the locale's display name via
// {{ 'kns_local.steel_hub.steel_name_…' | t | json }}) ONLY for steels whose ko /
// zh-TW name differs from English; `model` stays the English value every locale
// falls back to (and what steelAbbr derives the abbreviation from). Display code
// shows the locale name where one exists.
export function displayName(s) {
  return s && s.name ? s.name : (s ? s.model : '');
}

// Parse a data-sort string: a finite number, else a lowercased string.
export function cellSortValue(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s === '') return '';
  const n = Number(s);
  return Number.isFinite(n) && /^-?\d*\.?\d+$/.test(s) ? n : s.toLowerCase();
}

// Comparator for two raw cell values. dir = 1 asc, -1 desc. Numbers sort
// numerically (data-sort="-1" sinks to the bottom on asc); mixed types: numbers
// before strings.
export function compareCells(rawA, rawB, dir = 1) {
  const a = cellSortValue(rawA);
  const b = cellSortValue(rawB);
  // The SSR sentinel -1 marks missing / not-disclosed / unrated cells. They must
  // always sink to the bottom regardless of direction — never mix visually with
  // real low values (an honesty concern as well as a sort bug).
  const aMiss = a === -1, bMiss = b === -1;
  if (aMiss || bMiss) return aMiss && bMiss ? 0 : (aMiss ? 1 : -1);
  const an = typeof a === 'number';
  const bn = typeof b === 'number';
  let r;
  if (an && bn) r = a - b;
  else if (an) r = -1;            // numbers before strings
  else if (bn) r = 1;
  else r = a < b ? -1 : a > b ? 1 : 0;
  return r * dir;
}

// Filter predicate. state = { cats:Set<string>, hasKnives:boolean }.
export function matchesFilter(cat, count, state) {
  const catOk = !state.cats || state.cats.size === 0 || state.cats.has(cat);
  const knivesOk = !state.hasKnives || Number(count) > 0;
  return catOk && knivesOk;
}

// When either compared axis is an estimate (or has no basis), comparative
// phrasing must be qualified — never assert an estimate as hard fact.
export function verdictQualifier(basisA, basisB) {
  const soft = (b) => b == null || b === 'estimate';
  return (soft(basisA) || soft(basisB)) ? 'estimated' : '';
}

// Build the compare view-model from the parsed island for the given handles.
// Honesty: comp values pass through verbatim (already v:null for nd/unknown).
const AXES = [
  { key: 'edge', score: 'edge' },
  { key: 'tough', score: 'tough' },
  { key: 'corr', score: 'corr' },
  { key: 'sharp', score: 'sharp' },
];
export function buildCompareModel(island, handles) {
  const byH = new Map((island.steels || []).map((s) => [s.h, s]));
  const steels = handles.map((h) => byH.get(h)).filter(Boolean);
  if (steels.length < 2) return null;
  const axes = AXES.map(({ key }) => ({
    key,
    scores: steels.map((s) => (s.perf?.[key]?.s ?? null)),
    bases: steels.map((s) => (s.perf?.[key]?.b ?? null)),
  }));
  // Composition for the compare view is derived from steels by compositionColumns()
  // (honesty-gated) at render time — buildCompareModel only carries perf + hrc.
  return { steels, axes, hrc: steels.map((s) => s.hrc) };
}

// Bounded, honesty-safe TL;DR comparing the first two steels on edge vs sharpening.
// Localized sentence templates: the qualifier `q` (estimated) is computed by
// verdictQualifier; templates only choose wording. Default = English.
export const EN_V = {
  unrated: (a, b) => `${a} and ${b} — compare the rated axes below; some scores aren't rated yet.`,
  equal: (a, b, q) => `${a} and ${b} hold their edge about equally${q ? ' (estimated)' : ''} — see the axes below.`,
  verdict: (harder, easier, q) => `${harder} ${q ? 'is estimated to hold' : 'holds'} its edge longer${easier ? `; ${easier} is easier to sharpen` : ''}.`,
};
export const ZH_V = {
  unrated: (a, b) => `${a} 與 ${b}：下方比較已評分的項目；部分分數尚未評定。`,
  equal: (a, b, q) => `${a} 與 ${b} 的保刃力相近${q ? '（預估）' : ''}，詳見下方各項。`,
  verdict: (harder, easier, q) => `${harder}${q ? '預估保刃更持久' : '保刃更持久'}${easier ? `；${easier} 更易研磨` : ''}。`,
};
export const VERDICT = { en: EN_V, 'zh-TW': ZH_V };

export function templatedVerdict(model, t = EN_V) {
  const [a, b] = model.steels;
  const edge = model.axes.find((x) => x.key === 'edge');
  const sharp = model.axes.find((x) => x.key === 'sharp');
  if (!edge || edge.scores[0] == null || edge.scores[1] == null) return t.unrated(displayName(a), displayName(b));
  const q = !!verdictQualifier(edge.bases[0], edge.bases[1]);
  const harder = edge.scores[0] === edge.scores[1] ? null : (edge.scores[0] > edge.scores[1] ? a : b);
  if (!harder) return t.equal(displayName(a), displayName(b), q);
  const easier = sharp && sharp.scores[0] != null && sharp.scores[1] != null && sharp.scores[0] !== sharp.scores[1]
    ? (sharp.scores[0] > sharp.scores[1] ? a : b) : null;
  return t.verdict(displayName(harder), (easier && easier !== harder) ? displayName(easier) : null, q);
}

/* --------------- composition chart + HRC gauge + search (tested) --------- */

// Module-level HTML escaper (shared by the pure renderers and the DOM layer).
export function escHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Element metadata for the composition chart: [symbol, en name, zh name], in the
// canonical column order the per-steel page uses.
export const ELEMENTS = [
  ['C', 'Carbon', '碳'], ['Cr', 'Chromium', '鉻'], ['V', 'Vanadium', '釩'],
  ['Mo', 'Molybdenum', '鉬'], ['W', 'Tungsten', '鎢'], ['Co', 'Cobalt', '鈷'],
  ['Mn', 'Manganese', '錳'], ['Si', 'Silicon', '矽'], ['Ni', 'Nickel', '鎳'],
  ['Cu', 'Copper', '銅'], ['N', 'Nitrogen', '氮'], ['P', 'Phosphorus', '磷'],
  ['S', 'Sulfur', '硫'],
];
const ELEMENT_ORDER = ELEMENTS.map((e) => e[0]);
const ELEMENT_NAME = new Map(ELEMENTS.map(([sym, en, zh]) => [sym, { en, zh }]));

// Case-insensitive substring match; a blank query matches everything.
export function matchesSearch(text, q) {
  const needle = String(q == null ? '' : q).trim().toLowerCase();
  if (!needle) return true;
  return String(text == null ? '' : text).toLowerCase().includes(needle);
}

// Island steels whose model OR endonym matches the query.
export function filterByQuery(steels, q) {
  return (steels || []).filter((s) => matchesSearch(s.model, q) || matchesSearch(s.endonym, q));
}

// One steel's honest composition state for a symbol.
//   numeric  → { st:<value state>, v:Number }      (manufacturer/catalogue value)
//   nd       → { st:'not_disclosed', v:null }       (manufacturer won't say)
//   absent   → { st:'absent', v:null }              (element not in our data)
// The island already nulls v for not_disclosed/unknown, so we branch on st and
// never invent a number — the honesty invariant carried to the client.
function compCell(steel, sym) {
  const c = steel.comp?.[sym];
  if (!c) return { st: 'absent', v: null };
  if (c.v != null && Number.isFinite(c.v)) return { st: c.st || 'catalogue_value', v: c.v };
  return { st: c.st === 'not_disclosed' ? 'not_disclosed' : 'absent', v: null };
}

// Chart columns: element symbols (canonical order) where at least one compared
// steel discloses a NUMBER. A steel that doesn't disclose that element gets a
// non-numeric cell (nd marker or dash) — never a fabricated value.
export function compositionColumns(steels) {
  return ELEMENT_ORDER
    .map((sym) => ({ sym, cells: steels.map((s) => compCell(s, sym)) }))
    .filter((col) => col.cells.some((c) => c.v != null));
}

// true if a steel discloses at least one numeric composition value.
export function hasDisclosedComposition(steel) {
  return ELEMENT_ORDER.some((sym) => compCell(steel, sym).v != null);
}

// Linear vs √ scale: one dominant element (e.g. Cr 15% next to C 1%) crushes the
// small bars on a linear axis, so switch to √ when max/second > 4 (per-steel page).
export function chartScale(values) {
  const nums = (values || []).filter((v) => Number.isFinite(v) && v > 0).sort((a, b) => b - a);
  const max = nums[0] || 0;
  const second = nums[1] || 0;
  return { max, second, useSqrt: second > 0 && max / second > 4 };
}

// Bar height as an integer percent (0..100) under the chosen scale.
export function scalePct(v, max, useSqrt) {
  if (!(max > 0) || !Number.isFinite(v) || v <= 0) return 0;
  const f = useSqrt ? Math.sqrt : (x) => x;
  return Math.max(0, Math.min(100, Math.round((f(v) / f(max)) * 100)));
}

// HRC gauge scale: 55 baseline; extend past 65 only when a steel runs harder.
export function hrcScaleMax(hrcs) {
  const hi = (hrcs || []).reduce((m, h) => (h ? Math.max(m, h.hi ?? h.lo ?? 0) : m), 0);
  return hi > 65 ? hi + 2 : 65;
}
export function hrcPct(value, scaleMax) {
  const span = (scaleMax || 65) - 55;
  if (!(span > 0) || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(((value - 55) / span) * 100)));
}

// Composition dual/single bar chart as an HTML string (gold = steel A, blue = B).
// Honesty: not_disclosed → an "ND" stub with no height and no number; absent →
// a dash. A steel that discloses nothing gets an explicit note, not empty bars.
export function renderCompositionChartHTML(steels, opts = {}) {
  const { zh = false, t = {} } = opts;
  const esc = escHtml;
  const slot = (i) => (['a', 'b'][i] || 'b');
  const ndLabel = t.ndShort || (zh ? '未公開' : 'ND');
  const ndFull = t.nd || (zh ? '未公開' : 'not disclosed');
  const ndComp = t.ndComposition || (zh ? '製造商未公開成分' : 'composition not disclosed by the manufacturer');
  const note = steels
    .filter((s) => !hasDisclosedComposition(s))
    .map((s) => `<p class="kns-hub-cc__ndnote">${esc(displayName(s))} — ${esc(ndComp)}.</p>`)
    .join('');
  const cols = compositionColumns(steels);
  if (!cols.length) {
    return `<div class="kns-hub-cc kns-hub-cc--none">${note || `<p class="kns-hub-cc__ndnote">${esc(ndComp)}.</p>`}</div>`;
  }
  const values = cols.flatMap((c) => c.cells.map((x) => x.v).filter((v) => v != null));
  const { max, useSqrt } = chartScale(values);
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => {
    const raw = useSqrt ? Math.pow(f * Math.sqrt(max), 2) : max * f;
    const r = Math.round(raw * 100) / 100;
    return `<span>${r > 0 ? r + '%' : '0'}</span>`;
  }).join('');
  const keys = steels.map((s, i) =>
    `<span class="kns-hub-cc__key" data-slot="${slot(i)}"><i></i>${esc(displayName(s))}</span>`).join('');
  const colsHtml = cols.map((col) => {
    const bars = col.cells.map((c, i) => {
      if (c.v != null) {
        return `<span class="kns-hub-cc__bar" data-slot="${slot(i)}" data-v="${c.v}" data-h="${scalePct(c.v, max, useSqrt)}" style="height:0%"></span>`;
      }
      if (c.st === 'not_disclosed') {
        return `<span class="kns-hub-cc__bar kns-hub-cc__bar--nd" data-slot="${slot(i)}" title="${esc(ndFull)}" aria-label="${esc(ndFull)}"></span>`;
      }
      return `<span class="kns-hub-cc__bar kns-hub-cc__bar--absent" data-slot="${slot(i)}" aria-hidden="true"></span>`;
    }).join('');
    const vals = col.cells.map((c, i) => {
      if (c.v != null) return `<span data-slot="${slot(i)}">${esc(String(c.v))}%</span>`;
      if (c.st === 'not_disclosed') return `<span data-slot="${slot(i)}" class="kns-hub-cc__nd"><abbr title="${esc(ndFull)}">${esc(ndLabel)}</abbr></span>`;
      return `<span data-slot="${slot(i)}" class="kns-hub-cc__dash" aria-hidden="true">—</span>`;
    }).join('');
    const nm = ELEMENT_NAME.get(col.sym);
    const title = nm ? `${col.sym} · ${zh ? nm.zh : nm.en}` : col.sym;
    return `<div class="kns-hub-cc__col" title="${esc(title)}"><div class="kns-hub-cc__bars">${bars}</div>`
      + `<div class="kns-hub-cc__sym">${esc(col.sym)}</div><div class="kns-hub-cc__cellvals">${vals}</div></div>`;
  }).join('');
  // Stable accessible name (doesn't depend on the √ glyph, which AT pronounces
  // inconsistently); aria-pressed conveys on/off, title updated on toggle by the DOM layer.
  const sqrtAria = zh ? '平方根縮放（切換線性）' : 'Square-root scale (toggle linear)';
  const sqrtTitle = zh ? '目前：平方根縮放。點選切換為線性。' : 'Currently: √ scale. Click to switch to linear.';
  const scaleBtn = useSqrt
    ? `<button type="button" class="kns-hub-cc__scale" data-scale="sqrt" aria-pressed="true" aria-label="${esc(sqrtAria)}" title="${esc(sqrtTitle)}">${esc(t.sqrt || '√ scale')}</button>`
    : '';
  return `<div class="kns-hub-cc" data-mode="${steels.length > 1 ? 'dual' : 'single'}" data-max="${max}" data-sqrt="${useSqrt ? 1 : 0}">`
    + `<div class="kns-hub-cc__top"><div class="kns-hub-cc__keys">${keys}</div>${scaleBtn}</div>`
    + `<div class="kns-hub-cc__plot"><div class="kns-hub-cc__yaxis" aria-hidden="true">${ticks}</div>`
    + `<div class="kns-hub-cc__cols">${colsHtml}</div></div>${note}</div>`;
}

// HRC hardness gauge (single or dual) as an HTML string. Null HRC → "not rated".
export function renderHrcGaugeHTML(steels, opts = {}) {
  const { zh = false, t = {} } = opts;
  const esc = escHtml;
  const slot = (i) => (['a', 'b'][i] || 'b');
  const scaleMax = hrcScaleMax(steels.map((s) => s.hrc || null));
  const ticks = [55, 57, 59, 61, 63].map((v) => `<span>${v}</span>`).join('') + `<span>${scaleMax}+</span>`;
  const rows = steels.map((s, i) => {
    const h = s.hrc;
    if (!h || (h.lo == null && h.hi == null)) {
      return `<div class="kns-hub-hrc__row" data-slot="${slot(i)}"><span class="kns-hub-hrc__name">${esc(displayName(s))}</span>`
        + `<span class="kns-hub-hrc__track"></span><span class="kns-hub-hrc__val kns-hub-hrc__val--na">${esc(t.notRated || (zh ? '尚未評定' : 'not rated'))}</span></div>`;
    }
    const lo = h.lo ?? h.hi;
    const hi = h.hi ?? h.lo;
    const label = h.lo != null && h.hi == null ? `${lo}+ HRC` : hi > lo ? `${lo}–${hi} HRC` : `${lo} HRC`;
    return `<div class="kns-hub-hrc__row" data-slot="${slot(i)}"><span class="kns-hub-hrc__name">${esc(displayName(s))}</span>`
      + `<span class="kns-hub-hrc__track"><span class="kns-hub-hrc__rng" data-w="${hrcPct(hi, scaleMax)}" style="width:0%"></span>`
      + `<span class="kns-hub-hrc__lo" data-w="${hrcPct(lo, scaleMax)}" style="width:0%"></span></span>`
      + `<span class="kns-hub-hrc__val">${esc(label)}</span></div>`;
  }).join('');
  return `<div class="kns-hub-hrc"><div class="kns-hub-hrc__head">${esc(t.hardness || (zh ? '硬度' : 'Hardness'))}</div>`
    + `<div class="kns-hub-hrc__ticks" aria-hidden="true">${ticks}</div>${rows}</div>`;
}

/* ------------------------------- DOM layer ------------------------------- */

function initHub() {
  const root = document.querySelector('.kns-hub');
  if (!root) return;
  const locale = root.dataset.locale === 'zh-TW' ? 'zh-TW' : 'en';
  const T = {
    en: { edge: 'Edge retention', tough: 'Toughness', corr: 'Rust resistance', sharp: 'Sharpening ease',
          steels: 'steels', pickMore: 'Pick one more steel…', add: '+ Compare',
          notRated: 'not yet rated', nd: 'not disclosed', composition: 'Composition', hardness: 'Hardness',
          cats: { carbon: 'Carbon', stainless: 'Stainless', 'semi-stainless': 'Semi-stainless' },
          basis: { cited: 'cited', reasoned: 'reasoned', estimate: 'estimate' }, remove: 'Remove',
          sortAsc: 'table sorted ascending', sortDesc: 'table sorted descending',
          ndShort: 'ND', ndComposition: 'composition not disclosed by the manufacturer', sqrt: '√ scale',
          viewPage: 'View steel page →', refNote: 'Reference steel — not stocked', noMatch: 'No steels match', pickPrompt: 'Pick two steels to compare — search above, or tap them in the Map or Table.' },
    'zh-TW': { edge: '保刃性', tough: '韌性', corr: '抗鏽性', sharp: '易磨性',
          steels: '款鋼材', pickMore: '再選一款鋼材…', add: '＋ 比較',
          notRated: '尚未評分', nd: '未公開', composition: '成分', hardness: '硬度',
          cats: { carbon: '碳鋼', stainless: '不鏽鋼', 'semi-stainless': '半不鏽鋼' },
          basis: { cited: '引用', reasoned: '推估', estimate: '估計' }, remove: '移除',
          sortAsc: '表格已升冪排序', sortDesc: '表格已降冪排序',
          ndShort: '未公開', ndComposition: '製造商未公開成分', sqrt: '√ 縮放',
          viewPage: '查看鋼材頁面 →', refNote: '參考鋼材 — 非販售', noMatch: '沒有符合的鋼材', pickPrompt: '選兩款鋼材即可並排比較——用上方搜尋，或在「地圖」「表格」點選。' }
  }[locale];
  const esc = escHtml;
  const zhLoc = locale === 'zh-TW';
  const tChart = { nd: T.nd, ndShort: T.ndShort, ndComposition: T.ndComposition, sqrt: T.sqrt, hardness: T.hardness, notRated: T.notRated };
  const steelHref = (h) => (zhLoc ? '/zh' : '') + '/pages/steel/' + encodeURIComponent(h);
  // Animate any composition-chart bars + HRC gauge bars freshly injected into `box`.
  function animateCharts(box) {
    requestAnimationFrame(() => {
      box.querySelectorAll('.kns-hub-cc__bar[data-h]').forEach((b) => { b.style.height = b.dataset.h + '%'; });
      box.querySelectorAll('.kns-hub-hrc__lo, .kns-hub-hrc__rng').forEach((b) => { b.style.width = b.dataset.w + '%'; });
      box.querySelectorAll('.kns-hub-cmp__fill').forEach((f) => { f.style.width = (f.dataset.w || 0) + '%'; });
    });
  }
  // Wire the √/linear scale toggle for a composition chart inside `box`.
  function wireScaleToggle(box) {
    box.querySelectorAll('.kns-hub-cc').forEach((cc) => {
      const btn = cc.querySelector('.kns-hub-cc__scale');
      if (!btn) return;
      const max = parseFloat(cc.dataset.max) || 0;
      btn.addEventListener('click', () => {
        const useSqrt = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', useSqrt ? 'true' : 'false');
        btn.title = useSqrt
          ? (zhLoc ? '目前：平方根縮放。點選切換為線性。' : 'Currently: √ scale. Click to switch to linear.')
          : (zhLoc ? '目前：線性縮放。點選切換為平方根。' : 'Currently: linear scale. Click to switch to √ scale.');
        cc.dataset.sqrt = useSqrt ? '1' : '0';
        cc.querySelectorAll('.kns-hub-cc__bar[data-v]').forEach((b) => {
          b.style.height = scalePct(parseFloat(b.dataset.v), max, useSqrt) + '%';
        });
        const ticks = cc.querySelectorAll('.kns-hub-cc__yaxis span');
        [1, 0.75, 0.5, 0.25, 0].forEach((f, i) => {
          if (!ticks[i]) return;
          const raw = useSqrt ? Math.pow(f * Math.sqrt(max), 2) : max * f;
          const r = Math.round(raw * 100) / 100;
          ticks[i].textContent = r > 0 ? r + '%' : '0';
        });
      });
    });
  }

  let island = { steels: [] };
  try { island = JSON.parse(document.getElementById('kns-hub-data')?.textContent || '{"steels":[]}'); } catch (e) { /* island absent */ }
  const islandBy = new Map(island.steels.map((s) => [s.h, s]));

  /* ---- view-switcher (tablist, roving tabindex) ---- */
  const tabs = Array.from(root.querySelectorAll('.kns-hub__tab'));
  const panelOf = (tab) => document.getElementById(tab.getAttribute('aria-controls'));
  function selectTab(tab, focus) {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const p = panelOf(t); if (p) p.hidden = !on;
    });
    if (focus) tab.focus();
    if (tab.id) history.replaceState(null, '', '#' + tab.id.replace('kns-tab-', ''));
  }
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => selectTab(tab));
    tab.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const d = e.key === 'ArrowRight' ? 1 : -1;
        selectTab(tabs[(i + d + tabs.length) % tabs.length], true);
      }
    });
  });
  const hash = location.hash.replace('#', '');
  const initial = tabs.find((t) => t.id === 'kns-tab-' + hash) || tabs[0];
  if (initial) selectTab(initial);

  /* ---- filters ---- */
  const chips = Array.from(root.querySelectorAll('.kns-hub__chip'));
  const countEl = root.querySelector('.kns-hub__count');
  const state = { cats: new Set(), hasKnives: false, q: '' };
  const points = Array.from(root.querySelectorAll('.kns-hub-scatter__pt'));
  const rows = Array.from(root.querySelectorAll('.kns-hub-table tbody tr'));
  // Read the steel name from the anchor only — the +Compare button injected into
  // cells[0] later must NOT leak into the searchable text (it would desync the
  // table from the mobile cards, which key off the clean dataset.name).
  const rowName = (r) => { const c = r.cells[0]; if (!c) return ''; const a = c.querySelector('a'); return (a ? a.textContent : c.textContent).trim(); };
  function applyFilters() {
    let shown = 0;
    points.forEach((p) => {
      // Map points respond to category/stock chips only — the search box is a Table affordance.
      p.classList.toggle('is-dim', !matchesFilter(p.dataset.cat, p.dataset.productCount, state));
    });
    rows.forEach((r) => {
      const ok = matchesFilter(r.dataset.cat, r.dataset.productCount, state) && matchesSearch(rowName(r), state.q);
      r.hidden = !ok; if (ok) shown++;
    });
    root.querySelectorAll('.kns-hub-card').forEach((c) => {
      const ok = matchesFilter(c.dataset.cat, c.dataset.productCount, state) && matchesSearch(c.dataset.name, state.q);
      c.hidden = !ok;
    });
    if (countEl) countEl.textContent = `${shown} ${T.steels}`;
  }
  const tsearch = root.querySelector('.kns-hub-tsearch__input');
  if (tsearch) tsearch.addEventListener('input', () => { state.q = tsearch.value; applyFilters(); });
  chips.forEach((chip) => chip.addEventListener('click', () => {
    const f = chip.dataset.filter;
    if (f === 'all') { state.cats.clear(); state.hasKnives = false; }
    else if (f === 'has-knives') state.hasKnives = !state.hasKnives;
    else { state.cats.has(f) ? state.cats.delete(f) : state.cats.add(f); }
    chips.forEach((c) => {
      const on = c.dataset.filter === 'all'
        ? (state.cats.size === 0 && !state.hasKnives)
        : c.dataset.filter === 'has-knives' ? state.hasKnives : state.cats.has(c.dataset.filter);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
      c.classList.toggle('is-on', on);
    });
    applyFilters();
  }));
  applyFilters();

  /* ---- table sort ---- */
  const table = root.querySelector('.kns-hub-table');
  if (table) {
    const headRow = table.tHead.rows[0];
    const ths = Array.from(headRow.cells);
    const tbody = table.tBodies[0];
    let sortState = { col: -1, dir: 1 };
    ths.forEach((th, col) => {
      const btn = th.querySelector('button'); if (!btn) return;
      btn.addEventListener('click', () => {
        const dir = (sortState.col === col && sortState.dir === 1) ? -1 : 1;
        sortState = { col, dir };
        ths.forEach((t) => t.removeAttribute('aria-sort'));
        th.setAttribute('aria-sort', dir === 1 ? 'ascending' : 'descending');
        const sorted = Array.from(tbody.rows).sort((ra, rb) =>
          compareCells(cellVal(ra, col), cellVal(rb, col), dir));
        sorted.forEach((r) => tbody.appendChild(r));
        announce(dir === 1 ? T.sortAsc : T.sortDesc);
      });
    });
    function cellVal(row, col) {
      const cell = row.cells[col];
      return cell ? (cell.dataset.sort ?? cell.textContent.trim()) : '';
    }
  }

  /* ---- mobile cards (built from the table DOM; CSS shows them <1024px) ---- */
  buildCards();
  function buildCards() {
    if (!table) return;
    const labels = Array.from(table.tHead.rows[0].cells).map((c) => (c.querySelector('button')?.textContent || c.textContent).trim());
    const host = document.createElement('div'); host.className = 'kns-hub-cards';
    Array.from(table.tBodies[0].rows).forEach((r) => {
      const handle = r.dataset.handle;
      const nameTh = r.cells[0];
      const card = document.createElement('article');
      card.className = 'kns-hub-card';
      card.dataset.cat = r.dataset.cat; card.dataset.productCount = r.dataset.productCount;
      card.dataset.name = (nameTh.textContent || '').trim();
      const head = document.createElement('div'); head.className = 'kns-hub-card__head';
      head.innerHTML = `<span><span class="kns-hub-card__dot" data-cat="${r.dataset.cat}"></span> ${nameTh.innerHTML}</span>`;
      const grid = document.createElement('dl'); grid.className = 'kns-hub-card__grid';
      for (let i = 1; i < r.cells.length; i++) {
        const cell = document.createElement('div'); cell.className = 'kns-hub-card__cell';
        cell.innerHTML = `<dt>${labels[i]}</dt><dd>${r.cells[i].innerHTML}</dd>`;
        grid.appendChild(cell);
      }
      const actions = document.createElement('div'); actions.className = 'kns-hub-card__actions';
      actions.appendChild(makeCmpBtn(handle));
      card.append(head, grid, actions);
      host.appendChild(card);
    });
    table.parentElement.insertAdjacentElement('afterend', host);
    applyFilters(); // sync the freshly-built cards to any active filter/search
  }

  /* ---- detail sheet ---- */
  const sheet = root.querySelector('.kns-hub__sheet');
  const sheetBody = sheet?.querySelector('.kns-hub__sheet-body');
  let lastFocus = null;
  function openSheet(handle) {
    const s = islandBy.get(handle); if (!s || !sheet) return;
    lastFocus = document.activeElement;
    const cat = T.cats[s.cat] || s.cat.replace('-', ' ');
    const hrc = s.hrc ? `${s.hrc.lo ?? ''}${s.hrc.lo != null && s.hrc.hi == null ? '+' : ''}${s.hrc.hi != null ? '–' + s.hrc.hi : ''} HRC` : '';
    const stat = (k) => (s.perf?.[k]?.s ?? null);
    const line = ['edge', 'tough', 'corr', 'sharp'].map((k) => `${T[k]} ${stat(k) ?? '—'}`).join(' · ');
    const chart = renderCompositionChartHTML([s], { zh: zhLoc, t: tChart });
    const gauge = renderHrcGaugeHTML([s], { zh: zhLoc, t: tChart });
    sheetBody.innerHTML =
      `<h3 class="kns-hub__sheet-name" id="kns-sheet-name">${esc(displayName(s))}${s.endonym ? ` <span class="kns-hub-endonym" lang="ja">${esc(s.endonym)}</span>` : ''}</h3>` +
      `<p class="kns-hub__sheet-meta">${esc(cat)}${hrc ? ' · ' + esc(hrc) : ''}</p>` +
      `<p class="kns-hub__sheet-who">${esc(line)}</p>` +
      `<div class="kns-hub__sheet-chart">${chart}${gauge}</div>` +
      `<div class="kns-hub__sheet-actions">` +
        `<button class="kns-hub__btn kns-hub__btn--gold" data-add="${esc(handle)}">${esc(T.add)}</button>` +
        (s.st === 0
          ? `<span class="kns-hub__ref-note">${esc(T.refNote)}</span>`
          : `<a class="kns-hub__btn" href="${esc(steelHref(handle))}">${esc(T.viewPage)}</a>`) +
      `</div>`;
    sheet.hidden = false;
    animateCharts(sheetBody);
    wireScaleToggle(sheetBody);
    sheet.querySelector('[data-add]').addEventListener('click', () => { addToCompare(handle); closeSheet(); });
    sheet.querySelector('.kns-hub__sheet-close').focus();
  }
  function closeSheet() { if (sheet) { sheet.hidden = true; if (lastFocus) lastFocus.focus(); } }
  sheet?.querySelector('.kns-hub__sheet-close')?.addEventListener('click', closeSheet);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet(); });
  points.forEach((pt) => {
    pt.addEventListener('click', (e) => { e.preventDefault(); openSheet(pt.dataset.handle); });
    const c = pt.querySelector('circle');
    c?.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSheet(pt.dataset.handle); } });
  });

  /* ---- compare ---- */
  const comparePanel = document.getElementById('kns-hub-compare');
  const cmpBody = comparePanel ? comparePanel.querySelector('.kns-hub-compare__body') : null;
  const cmpInput = document.getElementById('kns-cmp-search');
  const cmpDD = document.getElementById('kns-cmp-dd');
  const cmpBadge = root.querySelector('.kns-hub__tab-badge');
  const STORE = 'kns-hub-compare';
  const cap = () => 2; // v1: 1-on-1 compare (paired bars); 3-up deferred to v1.1
  let picks = loadPicks();
  savePicks();          // persist any trim applied at load (e.g. a 3-pick desktop session opened on mobile)
  function loadPicks() { try { return JSON.parse(sessionStorage.getItem(STORE) || '[]').filter((h) => islandBy.has(h)).slice(-cap()); } catch (e) { return []; } }
  function savePicks() { try { sessionStorage.setItem(STORE, JSON.stringify(picks)); } catch (e) { /* private mode */ } }
  function makeCmpBtn(handle) {
    const b = document.createElement('button');
    b.className = 'kns-hub-row-cmp'; b.type = 'button';
    b.textContent = T.add; b.dataset.add = handle;
    b.addEventListener('click', () => addToCompare(handle));
    return b;
  }
  function addToCompare(handle) {
    if (!islandBy.has(handle)) return;
    if (picks.includes(handle)) return;
    picks.push(handle);
    while (picks.length > cap()) picks.shift();
    savePicks(); renderCompare();
    const tab = document.getElementById('kns-tab-compare');
    if (tab && picks.length >= 2) selectTab(tab);
  }
  function removeFromCompare(handle) { picks = picks.filter((h) => h !== handle); savePicks(); renderCompare(); }
  // inject +Compare into each table row (progressive enhancement)
  rows.forEach((r) => { const th = r.cells[0]; if (th) th.appendChild(makeCmpBtn(r.dataset.handle)); });

  /* compare picker — typeahead over the island (the in-panel way to pick steels) */
  let ddActive = -1;
  const sortedSteels = island.steels.slice().sort((a, b) => String(a.model).localeCompare(String(b.model)));
  function cmpCandidates() { return sortedSteels.filter((s) => !picks.includes(s.h)); }
  function renderCmpDropdown() {
    if (!cmpInput || !cmpDD) return;
    const q = cmpInput.value;
    const matches = filterByQuery(cmpCandidates(), q).slice(0, 12);
    ddActive = -1;
    cmpInput.removeAttribute('aria-activedescendant');
    if (!matches.length) {
      if (q.trim()) {
        // Keep the listbox non-empty per the ARIA combobox pattern (disabled option),
        // and announce the no-match state via the polite live region.
        cmpDD.innerHTML = `<li class="kns-hub-compare__dd-empty" role="option" aria-disabled="true">${esc(T.noMatch)}</li>`;
        cmpDD.hidden = false; cmpInput.setAttribute('aria-expanded', 'true');
        announce(T.noMatch);
      } else { closeCmpDropdown(); }
      return;
    }
    cmpDD.innerHTML = matches.map((s, i) =>
      `<li role="option" id="kns-cmp-opt-${i}" data-h="${esc(s.h)}">${esc(displayName(s))}${s.endonym ? `<em lang="ja">${esc(s.endonym)}</em>` : ''}</li>`).join('');
    cmpDD.hidden = false;
    cmpInput.setAttribute('aria-expanded', 'true');
    cmpDD.querySelectorAll('li[data-h]').forEach((li) => {
      li.addEventListener('mousedown', (e) => { e.preventDefault(); pickFromDropdown(li.dataset.h); });
    });
  }
  function pickFromDropdown(h) {
    addToCompare(h);
    if (cmpInput) cmpInput.value = '';
    closeCmpDropdown();
    if (picks.length < 2 && cmpInput) cmpInput.focus();
  }
  function closeCmpDropdown() {
    if (!cmpDD) return;
    cmpDD.hidden = true; ddActive = -1;
    if (cmpInput) { cmpInput.setAttribute('aria-expanded', 'false'); cmpInput.removeAttribute('aria-activedescendant'); }
  }
  if (cmpInput && cmpDD) {
    cmpInput.addEventListener('input', renderCmpDropdown);
    cmpInput.addEventListener('focus', renderCmpDropdown);
    cmpInput.addEventListener('keydown', (e) => {
      const opts = Array.from(cmpDD.querySelectorAll('li[data-h]'));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!opts.length) return;
        e.preventDefault();
        ddActive = (ddActive + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
        opts.forEach((o, i) => o.classList.toggle('is-active', i === ddActive));
        cmpInput.setAttribute('aria-activedescendant', opts[ddActive].id);
        opts[ddActive].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        if (ddActive >= 0 && opts[ddActive]) { e.preventDefault(); pickFromDropdown(opts[ddActive].dataset.h); }
      } else if (e.key === 'Escape') { closeCmpDropdown(); }
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('.kns-hub-compare__picker')) closeCmpDropdown(); });
    // Tab-away closes the listbox for keyboard users (option clicks use mousedown
    // + preventDefault, so selecting never triggers this blur).
    cmpInput.addEventListener('blur', () => closeCmpDropdown());
  }

  function renderCompare() {
    if (cmpBadge) { cmpBadge.textContent = String(picks.length); cmpBadge.hidden = picks.length === 0; }
    if (!cmpBody) return;
    const pickRow = picks.map((h, i) => {
      const s = islandBy.get(h); const slot = ['a', 'b'][i] || 'b';
      return `<span class="kns-hub-compare__slot" data-slot="${slot}">${esc(displayName(s))} <button type="button" data-remove="${esc(h)}" aria-label="${esc(T.remove)} ${esc(displayName(s))}">✕</button></span>`;
    }).join('');
    const pick = picks.length
      ? `<div class="kns-hub-compare__pick">${pickRow}${picks.length < 2 ? `<span class="kns-hub-compare__hint">${esc(T.pickMore)}</span>` : ''}</div>`
      : '';
    let result = `<p class="kns-hub-compare__empty">${esc(T.pickPrompt)}</p>`;
    const model = buildCompareModel(island, picks);
    if (model) result = renderCompareResult(model);
    cmpBody.innerHTML = pick + result;
    cmpBody.querySelectorAll('[data-remove]').forEach((b) => b.addEventListener('click', () => removeFromCompare(b.dataset.remove)));
    animateCharts(cmpBody);
    wireScaleToggle(cmpBody);
  }
  function renderCompareResult(model) {
    const slots = ['a', 'b'];
    const heads = model.steels.map((s, i) =>
      `<div class="kns-hub-cmp__head" data-slot="${slots[i]}"><div class="kns-hub-cmp__head-name">${esc(displayName(s))}${s.endonym ? ` <span class="kns-hub-endonym" lang="ja">${esc(s.endonym)}</span>` : ''}</div>` +
      `<div class="kns-hub-cmp__head-meta">${esc(T.cats[s.cat] || s.cat.replace('-', ' '))}${s.hrc ? ` · ${s.hrc.lo ?? ''}${s.hrc.lo != null && s.hrc.hi == null ? '+' : ''}${s.hrc.hi != null ? '–' + s.hrc.hi : ''} HRC` : ''}</div></div>`).join('');
    const axes = model.axes.map((ax) => {
      const anyRated = ax.scores.some((v) => v != null);
      if (!anyRated) return '';
      const basis = ax.bases.find((b) => b) || null;
      const axRows = ax.scores.map((sc, i) =>
        `<div class="kns-hub-cmp__row" data-slot="${slots[i]}"><span class="kns-hub-cmp__tag">${esc(displayName(model.steels[i]))}</span>` +
        `<span class="kns-hub-cmp__track"><span class="kns-hub-cmp__fill" data-w="${sc != null ? sc * 10 : 0}"></span></span>` +
        `<span class="kns-hub-cmp__val">${sc != null ? sc : '—'}</span></div>`).join('');
      return `<div class="kns-hub-cmp__axis"><div class="kns-hub-cmp__axis-label"><span>${esc(T[ax.key])}</span>` +
        (basis ? `<span class="kns-hub-cmp__basis" data-basis="${basis}">${esc(T.basis[basis] || basis)}</span>` : '') + `</div>${axRows}</div>`;
    }).join('');
    const chart = renderCompositionChartHTML(model.steels, { zh: zhLoc, t: tChart });
    const gauge = renderHrcGaugeHTML(model.steels, { zh: zhLoc, t: tChart });
    const verdict = `<div class="kns-hub-cmp__verdict">${esc(templatedVerdict(model, VERDICT[locale]))}</div>`;
    const perfLbl = zhLoc ? '表現評分' : 'Performance';
    return verdict + `<div class="kns-hub-cmp__heads">${heads}</div>`
      + `<section class="kns-hub-cmp__block"><h4 class="kns-hub-cmp__section-h">${esc(T.composition)}</h4>${chart}${gauge}</section>`
      + `<section class="kns-hub-cmp__block"><h4 class="kns-hub-cmp__section-h">${esc(perfLbl)}</h4>${axes}</section>`;
  }
  renderCompare();

  /* ---- a11y live region ---- */
  let live = root.querySelector('.kns-hub__live');
  if (!live) { live = document.createElement('div'); live.className = 'kns-hub__live'; live.setAttribute('aria-live', 'polite'); live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)'; root.appendChild(live); }
  function announce(msg) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); }

  /* ---- interactive 3D/2D map (progressive enhancement over the SSR scatter) ---- */
  function initMap() {
    const panel = document.getElementById('kns-hub-map');
    const mount = document.getElementById('kns-hub-map-mount');
    const controls = panel && panel.querySelector('.kns-hub-map__controls');
    const baseline = panel && panel.querySelector('.kns-hub-map__baseline');
    const tip = document.getElementById('kns-hub-map-tip');
    if (!panel || !mount || !controls) return; // shell absent -> keep the SSR scatter

    const midOf = (hrc) => {
      if (!hrc) return null;
      const lo = hrc.lo, hi = hrc.hi;
      if (typeof lo !== 'number' && typeof hi !== 'number') return null;
      const l = typeof lo === 'number' ? lo : hi;
      const h = typeof hi === 'number' ? hi : lo;
      return (l + h) / 2;
    };
    const steels = island.steels.filter((s) => s.st !== 0).map((s) => ({
      h: s.h, model: s.model, name: s.name || '', abbr: steelAbbr(s.h, s.model), cat: s.cat,
      hrcLo: s.hrc && s.hrc.lo, hrcHi: s.hrc && s.hrc.hi, hrcMid: midOf(s.hrc),
      tough: (s.perf && s.perf.tough && typeof s.perf.tough.s === 'number') ? s.perf.tough.s : null,
      price: typeof s.p === 'number' ? s.p : null,
      count: typeof s.k === 'number' ? s.k : 0,
    })).filter((s) => Number.isFinite(s.hrcMid));
    // No real price anywhere -> a price-height map is meaningless; keep the SSR scatter (honesty).
    if (!steels.some((s) => Number.isFinite(s.price))) return;

    const cfg = mapConfig(steels);
    const byH = new Map(steels.map((s) => [s.h, s]));
    // The map honours the category + "in stock" chips (the search box stays a Table
    // affordance). cfg is computed over ALL steels, so filtering hides points without
    // shifting the axes. "In stock" keys off the in-stock count (s.count), R1 C1.
    function activeSteels() { return steels.filter((s) => matchesFilter(s.cat, s.count, state)); }
    const labels = zhLoc
      ? { hardness: '硬度（HRC）→', toughness: '韌性 →', price: '↑ 典型價格（澳幣）' }
      : { hardness: 'Hardness (HRC) →', toughness: 'Toughness →', price: '↑ Typical price (AUD)' };
    const ariaLabel = zhLoc ? '互動式鋼材地圖：硬度、韌性與典型價格' : 'Interactive steel map: hardness, toughness and typical price';

    const rotEl = document.getElementById('kns-map-rot');
    const tiltEl = document.getElementById('kns-map-tilt');
    const zoomEl = document.getElementById('kns-map-zoom');
    const seg = panel.querySelector('.kns-hub-map__seg');
    const view = { mode: '3d', rot: rotEl ? +rotEl.value : -22, tilt: tiltEl ? +tiltEl.value : 52, zoom: 1 };
    let svg = null, sceneG = null;

    function rebuildScene() {
      if (!sceneG) return;
      const active = activeSteels();
      sceneG.innerHTML = view.mode === '2d'
        ? buildScene2d(active, cfg, { labels })
        : buildScene3d(active, cfg, { rot: view.rot, tilt: view.tilt, labels });
      setMapAriaLabels(); // spheres are recreated -> re-apply intrinsic names
      syncValueText();
    }
    function applyZoom() { if (sceneG) sceneG.setAttribute('transform', mapSceneTransform(view.mode, view.zoom)); syncValueText(); }
    function fullRender() {
      mount.innerHTML = buildMapSvg(activeSteels(), cfg, { mode: view.mode, rot: view.rot, tilt: view.tilt, zoom: view.zoom, labels, ariaLabel });
      svg = mount.querySelector('.kns-hub-map__svg');
      sceneG = mount.querySelector('.kns-hub-map__scene');
      setMapAriaLabels();
    }
    function syncControls() {
      const is3d = view.mode === '3d';
      if (svg) svg.setAttribute('data-mode', view.mode); // keep the shell attr in sync on toggle
      panel.querySelectorAll('.kns-hub-map__seg-btn').forEach((b) => b.setAttribute('aria-pressed', b.dataset.mode === view.mode ? 'true' : 'false'));
      panel.querySelectorAll('.kns-hub-map__ctl[data-ctl]').forEach((c) => { c.hidden = !is3d; }); // rotate/tilt apply to 3D only
    }

    function fmtHrc(d) { return (d.hrcHi != null && d.hrcLo != null && d.hrcHi !== d.hrcLo) ? d.hrcLo + '–' + d.hrcHi : String(d.hrcMid); }
    function showTip(g, clientX, clientY) {
      const d = byH.get(g.dataset.h); if (!d || !tip) return;
      const cat = (T.cats && T.cats[d.cat]) || d.cat;
      const price = Number.isFinite(d.price) ? '$' + d.price : (zhLoc ? '價格未知' : 'price n/a');
      const tough = Number.isFinite(d.tough) ? d.tough + '/10' : '—';
      const nm = d.name || d.model; // BUILD STEEL-NAMES: island display name where keyed
      tip.innerHTML = zhLoc
        ? '<b>' + esc(nm) + '</b><div class="row">硬度 <span>' + fmtHrc(d) + ' HRC</span> · 韌性 <span>' + tough + '</span></div><div class="row">' + esc(cat) + ' · 典型 <span>' + price + '</span> · <span>' + d.count + '</span> 現貨</div>'
        : '<b>' + esc(nm) + '</b><div class="row">Hardness <span>' + fmtHrc(d) + ' HRC</span> · Toughness <span>' + tough + '</span></div><div class="row">' + esc(cat) + ' · <span>' + price + '</span> typical · <span>' + d.count + '</span> in stock</div>';
      const rect = panel.getBoundingClientRect();
      tip.style.left = (clientX - rect.left + 14) + 'px';
      tip.style.top = (clientY - rect.top + 14) + 'px';
      tip.classList.add('is-on');
    }
    function hideTip() { if (tip) tip.classList.remove('is-on'); }
    // One bilingual sentence per steel — reused for the focus announce AND the
    // per-sphere aria-label (so a focused sphere has an intrinsic name, not just a
    // live-region echo; a11y M1). Plain text (set via textContent/setAttribute).
    function labelFor(d) {
      const cat = (T.cats && T.cats[d.cat]) || d.cat;
      const nm = d.name || d.model; // BUILD STEEL-NAMES: island display name where keyed
      const price = Number.isFinite(d.price) ? '$' + d.price : (zhLoc ? '價格未知' : 'price not available');
      const tough = d.tough == null ? '—' : d.tough;
      return zhLoc
        ? nm + '：' + fmtHrc(d) + ' HRC、韌性 ' + tough + '/10、' + cat + '、典型 ' + price + '、現貨 ' + d.count
        : nm + ': ' + fmtHrc(d) + ' HRC, toughness ' + tough + '/10, ' + cat + ', typical ' + price + ', ' + d.count + ' in stock';
    }
    function announceSteel(g) { const d = byH.get(g.dataset.h); if (d) announce(labelFor(d)); }
    function setMapAriaLabels() {
      mount.querySelectorAll('.kns-hub-map__pt').forEach((g) => { const d = byH.get(g.dataset.h); if (d) g.setAttribute('aria-label', labelFor(d)); });
    }
    function syncValueText() {
      if (rotEl) rotEl.setAttribute('aria-valuetext', Math.round(view.rot) + '°');
      if (tiltEl) tiltEl.setAttribute('aria-valuetext', Math.round(view.tilt) + '°');
      if (zoomEl) zoomEl.setAttribute('aria-valuetext', view.zoom.toFixed(2) + '×');
    }

    // delegated events on the mount (survive scene re-renders)
    mount.addEventListener('mousemove', (e) => { const g = e.target.closest('.kns-hub-map__pt'); if (g) showTip(g, e.clientX, e.clientY); else hideTip(); });
    mount.addEventListener('mouseleave', hideTip);
    mount.addEventListener('focusin', (e) => { const g = e.target.closest('.kns-hub-map__pt'); if (!g) return; const r = g.getBoundingClientRect(); showTip(g, r.left + r.width / 2, r.top + r.height / 2); announceSteel(g); });
    mount.addEventListener('focusout', hideTip);
    mount.addEventListener('click', (e) => { const g = e.target.closest('.kns-hub-map__pt'); if (g && typeof openSheet === 'function') openSheet(g.dataset.h); });
    mount.addEventListener('keydown', (e) => { if (e.key !== 'Enter' && e.key !== ' ') return; const g = e.target.closest('.kns-hub-map__pt'); if (g && typeof openSheet === 'function') { e.preventDefault(); openSheet(g.dataset.h); } });

    // controls
    if (seg) seg.addEventListener('click', (e) => { const b = e.target.closest('[data-mode]'); if (!b) return; view.mode = b.dataset.mode; rebuildScene(); applyZoom(); syncControls(); });
    if (rotEl) rotEl.addEventListener('input', () => { view.rot = +rotEl.value; rebuildScene(); });
    if (tiltEl) tiltEl.addEventListener('input', () => { view.tilt = +tiltEl.value; rebuildScene(); });
    if (zoomEl) zoomEl.addEventListener('input', () => { view.zoom = +zoomEl.value; applyZoom(); });
    // category + "in stock" chips filter the map too (their own handler updates `state`
    // first; this listener runs after and re-renders the scene with the active set).
    chips.forEach((chip) => chip.addEventListener('click', () => rebuildScene()));

    // drag = rotate+tilt (3D only); wheel = zoom (scoped to the svg, preventDefault)
    let drag = false, x0 = 0, y0 = 0, r0 = 0, t0 = 0;
    mount.addEventListener('pointerdown', (e) => {
      if (view.mode !== '3d' || !e.target.closest('.kns-hub-map__svg')) return;
      drag = true; x0 = e.clientX; y0 = e.clientY; r0 = view.rot; t0 = view.tilt;
      if (svg) { svg.classList.add('is-dragging'); if (svg.setPointerCapture) { try { svg.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ } } }
    });
    mount.addEventListener('pointermove', (e) => {
      if (!drag) return;
      view.rot = Math.max(-70, Math.min(70, r0 + (e.clientX - x0) * 0.4));
      view.tilt = Math.max(18, Math.min(82, t0 - (e.clientY - y0) * 0.3));
      if (rotEl) rotEl.value = view.rot;
      if (tiltEl) tiltEl.value = view.tilt;
      rebuildScene();
    });
    const endDrag = () => { drag = false; if (svg) svg.classList.remove('is-dragging'); };
    mount.addEventListener('pointerup', endDrag);
    mount.addEventListener('pointercancel', endDrag);
    mount.addEventListener('wheel', (e) => {
      if (!e.target.closest('.kns-hub-map__svg')) return;
      e.preventDefault();
      view.zoom = Math.max(0.7, Math.min(1.8, view.zoom - e.deltaY * 0.0012));
      if (zoomEl) zoomEl.value = view.zoom;
      applyZoom();
    }, { passive: false });

    // mount: reveal controls + map, hide the SSR scatter baseline (progressive enhancement)
    fullRender();
    syncValueText();
    syncControls();
    controls.hidden = false;
    mount.hidden = false;
    if (baseline) baseline.hidden = true;

    // Honesty caption: steels without an in-stock price aren't plotted (no fabricated
    // height) — point to the complete Table rather than silently dropping them.
    const noPrice = steels.filter((s) => !Number.isFinite(s.price)).length;
    if (noPrice > 0 && !panel.querySelector('.kns-hub-map__pricenote')) {
      const note = document.createElement('p');
      note.className = 'kns-hub__hint kns-hub-map__pricenote';
      note.textContent = zhLoc
        ? '部分鋼材尚無現貨定價，未顯示於地圖——完整資料見「表格」。'
        : 'Some steels have no in-stock pricing and aren’t plotted — see the Table for the full list.';
      mount.insertAdjacentElement('afterend', note);
    }
  }
  initMap();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHub);
  else initHub();
}
